import React from 'react'
import Head from 'next/head'
import dayjs from 'dayjs'
import {
  Alert,
  Button,
  Descriptions,
  List,
  Result,
  Select,
  Show,
  Skeleton,
  Table,
} from '@pankod/refine-antd'
import {
  IResourceComponentsProps,
  LogicalFilter,
  useCan,
  useNavigation,
  useOne,
  useResource,
} from '@pankod/refine-core'

import Filter, { TFilterForm } from '@components/ContentFilter'
import { kapabilitasMapper } from '@components/DataTableParticipants/blocks/utils'
import { Participant } from '@resources/lms-management/participant-management/type'
import {
  PROGRAM_STATE,
  TLMSProgram,
  TLMSProgramDetail,
} from '@resources/lms-management/program/types'
import { TCommonResponse, TLMSCommonError } from 'src/interfaces/common'
import useTableWithMeta from 'src/hooks/useTableWithMeta'
import { ParticipantEditor } from './participant-editor'
import {
  canEditPrimerParticipants,
  PRIMER_PARTICIPANT_RESOURCE,
  PRIMER_PARTICIPANT_TITLE,
} from './utils'

const AccessGuard = ({
  action,
  children,
}: {
  action: 'list' | 'show'
  children: React.ReactNode
}) => {
  const { data, isLoading } = useCan({
    resource: PRIMER_PARTICIPANT_RESOURCE,
    action,
  })
  if (isLoading) return <Skeleton active />
  if (!data?.can) return <Result status="403" title="Akses ditolak" />
  return <>{children}</>
}

const ProgramList = () => {
  const { showUrl, push } = useNavigation()
  const { tableProps, searchFormProps, filters, tableQueryResult } =
    useTableWithMeta<
      TLMSProgram,
      TLMSCommonError,
      TFilterForm<{
        name?: string
        status?: PROGRAM_STATE.RUNNING | PROGRAM_STATE.ENDED
      }>
    >({
      resource: 'programs',
      dataProviderName: 'lms',
      initialSorter: [{ field: 'endDate', order: 'desc' }],
      onSearch: ({ name, status }) => [
        { field: 'status', operator: 'eq', value: status },
        { field: 'name', operator: 'eq', value: name },
        { field: 'page', operator: 'eq', value: 1 },
      ],
    })

  return (
    <List title={PRIMER_PARTICIPANT_TITLE} canCreate={false}>
      <Head>
        <title>LMS Admin | {PRIMER_PARTICIPANT_TITLE}</title>
      </Head>
      <Alert
        className="mb-4"
        type="info"
        showIcon
        message="Pilih program berstatus berjalan atau selesai untuk mengubah data primer partisipan."
      />
      <Filter
        formProps={searchFormProps}
        filters={filters as LogicalFilter[]}
        layout="vertical"
        size="small"
        items={[
          {
            label: 'Status',
            name: 'status',
            component: (
              <Select
                placeholder="Filter status"
                allowClear
                options={[
                  { label: 'Berjalan', value: PROGRAM_STATE.RUNNING },
                  { label: 'Selesai', value: PROGRAM_STATE.ENDED },
                ]}
              />
            ),
          },
          { label: 'Nama Program', name: 'name' },
        ]}
      />
      {tableQueryResult.isError ? (
        <Alert
          type="error"
          message="Gagal memuat daftar program"
          action={
            <Button onClick={() => void tableQueryResult.refetch()}>
              Coba Lagi
            </Button>
          }
        />
      ) : null}
      <Table
        {...tableProps}
        rowKey="id"
        scroll={{ x: 800 }}
        locale={{ emptyText: 'Tidak ada program' }}
      >
        <Table.Column<TLMSProgram> title="ID" dataIndex="id" />
        <Table.Column<TLMSProgram> title="Nama Program" dataIndex="name" />
        <Table.Column<TLMSProgram> title="Status" dataIndex="status" />
        <Table.Column<TLMSProgram>
          title="Periode"
          render={(_, record) =>
            `${dayjs(record.startDate).format('DD MMM YYYY')} — ${dayjs(record.endDate).format('DD MMM YYYY')}`
          }
        />
        <Table.Column<TLMSProgram>
          title="Aksi"
          render={(_, record) => (
            <Button
              disabled={!canEditPrimerParticipants(record.status)}
              onClick={() =>
                push(showUrl(PRIMER_PARTICIPANT_RESOURCE, record.id))
              }
            >
              Pilih Program
            </Button>
          )}
        />
      </Table>
    </List>
  )
}

const ParticipantList = ({
  programId,
  program,
}: {
  programId: string
  program: TLMSProgramDetail
}) => {
  const [participant, setParticipant] = React.useState<Participant | null>(null)
  const [editorVisible, setEditorVisible] = React.useState(false)
  const { tableProps, searchFormProps, filters, tableQueryResult } =
    useTableWithMeta<
      Participant,
      TLMSCommonError,
      TFilterForm<{ keywords?: string }>
    >({
      resource: `programs/${programId}/participant-consolidation`,
      dataProviderName: 'lms',
      syncWithLocation: true,
      onSearch: ({ keywords }) => [
        { field: 'keywords', operator: 'eq', value: keywords },
      ],
    })

  return (
    <>
      <Descriptions bordered column={1} size="small" className="mb-4">
        <Descriptions.Item label="ID Program">{programId}</Descriptions.Item>
        <Descriptions.Item label="Nama Program">
          {program.name}
        </Descriptions.Item>
        <Descriptions.Item label="Status Program">
          {program.status}
        </Descriptions.Item>
      </Descriptions>
      <Filter
        formProps={searchFormProps}
        filters={filters as LogicalFilter[]}
        size="small"
        items={[
          {
            label: 'Cari Partisipan',
            name: 'keywords',
            placeholder: 'Ketik user ID, email, atau nama',
          },
        ]}
      />
      {tableQueryResult.isError ? (
        <Alert
          type="error"
          message="Gagal memuat daftar partisipan"
          action={
            <Button onClick={() => void tableQueryResult.refetch()}>
              Coba Lagi
            </Button>
          }
        />
      ) : null}
      <Table
        {...tableProps}
        rowKey="userId"
        scroll={{ x: 900 }}
        locale={{ emptyText: 'Tidak ada partisipan' }}
      >
        <Table.Column<Participant> title="User ID" dataIndex="userId" />
        <Table.Column<Participant> title="Email" dataIndex="email" />
        <Table.Column<Participant> title="Nama" dataIndex="name" />
        <Table.Column<Participant>
          title="Program Lokal"
          dataIndex="programLocalName"
        />
        <Table.Column<Participant>
          title="Kapabilitas"
          dataIndex="kapabilitas"
          render={(value: string) => kapabilitasMapper(value)}
        />
        <Table.Column<Participant>
          title="Aksi"
          render={(_, record) => (
            <Button
              disabled={!!participant}
              onClick={() => {
                setParticipant(record)
                setEditorVisible(true)
              }}
            >
              Ubah Data Primer
            </Button>
          )}
        />
      </Table>
      {participant ? (
        <ParticipantEditor
          key={`${programId}:${participant.userId}`}
          participant={participant}
          programId={programId}
          programStatus={program.status}
          visible={editorVisible}
          onClose={() => setEditorVisible(false)}
          onSaved={() => setEditorVisible(false)}
          onAfterClose={() => {
            if (!editorVisible) setParticipant(null)
          }}
        />
      ) : null}
    </>
  )
}

const ProgramParticipants = () => {
  const { id } = useResource()
  const programId = id == null ? '' : String(id)
  const { data, isLoading, isError, refetch } = useOne<
    TCommonResponse<TLMSProgramDetail>,
    TLMSCommonError
  >({
    resource: 'programs',
    id: programId,
    dataProviderName: 'lms',
    queryOptions: { enabled: !!programId, retry: false },
  })
  const program = data?.data?.data

  return (
    <Show title={PRIMER_PARTICIPANT_TITLE} headerButtons={<></>}>
      <Head>
        <title>LMS Admin | {PRIMER_PARTICIPANT_TITLE}</title>
      </Head>
      {isLoading ? (
        <Skeleton active />
      ) : isError || !program ? (
        <Alert
          type="error"
          message="Gagal memuat program"
          action={<Button onClick={() => void refetch()}>Coba Lagi</Button>}
        />
      ) : !canEditPrimerParticipants(program.status) ? (
        <Alert
          type="warning"
          showIcon
          message="Data primer hanya dapat diubah pada program berjalan atau selesai"
        />
      ) : (
        <ParticipantList
          key={programId}
          programId={programId}
          program={program}
        />
      )}
    </Show>
  )
}

export const PrimerParticipantsList: React.FC<
  IResourceComponentsProps
> = () => (
  <AccessGuard action="list">
    <ProgramList />
  </AccessGuard>
)

export const PrimerParticipantsShow: React.FC<
  IResourceComponentsProps
> = () => (
  <AccessGuard action="show">
    <ProgramParticipants />
  </AccessGuard>
)
